import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { OfferSetListService } from '../../../../service/offer-set-list.service';
import { OfferSetListItem } from '../../../../model/offer-set-list-item.model';
import { OfferSetListPagination } from '../../../../model/offer-set-list-response.model';
import { OfferSetBulkActionRequest } from '../../../../model/offer-set-bulk-action.model';
import { environment } from '../../../../../environments/environment';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {OfferSetRegistryService} from '../../../../service/offer-set-registry-service';

interface PresetTab {
  label: string;
  preset: string;
  status: string;
  condition: string;
  visibility: string;
}

interface FilterOption {
  label: string;
  value: string;
}

@Component({
  selector: 'auto-offer-set-list',
  templateUrl: './offer-set-list.component.html',
  styleUrls: ['./offer-set-list.component.scss'],
  standalone: false
})
export class OfferSetListComponent implements OnInit, OnDestroy {
  accountId: number;
  items: OfferSetListItem[] = [];
  pagination: OfferSetListPagination = {
    page: 1,
    pageSize: 50,
    totalItems: 0,
    totalPages: 0
  };
  isLoading = false;
  selectedItems: OfferSetListItem[] = [];
  allSelected = false;
  searchTerm = '';
  activePreset = 'new_active';
  selectedBulkAction = '';
  selectedStatus = 'active';
  selectedCondition = 'new';
  selectedVisibility = 'full_visibility';

  // Disclosure dialog state
  disclosureDialogVisible = false;
  disclosureTitle = '';
  disclosureHtml: SafeHtml | string = '';

  presetTabs: PresetTab[] = [
    { label: 'New · Active', preset: 'new_active', status: 'active', condition: 'new', visibility: 'full_visibility' },
    { label: 'New · Inactive', preset: 'new_inactive', status: 'inactive', condition: 'new', visibility: 'full_visibility' },
    { label: 'Used · Active', preset: 'used_active', status: 'active', condition: 'used', visibility: 'full_visibility' },
    { label: 'Used · Inactive', preset: 'used_inactive', status: 'inactive', condition: 'used', visibility: 'full_visibility' },
    { label: 'All Offer Sets', preset: 'all', status: 'any', condition: 'any', visibility: 'any' }
  ];

  statusOptions: FilterOption[] = [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
    { label: 'Any', value: 'any' }
  ];

  conditionOptions: FilterOption[] = [
    { label: 'New', value: 'new' },
    { label: 'Used', value: 'used' },
    { label: 'Any', value: 'any' }
  ];

  visibilityOptions: FilterOption[] = [
    { label: 'Full Visibility', value: 'full_visibility' },
    { label: 'Inventory Only', value: 'inventory_only' },
    { label: 'Any', value: 'any' }
  ];

  bulkActionOptions: FilterOption[] = [
    { label: 'Bulk Action: Select', value: '' },
    { label: 'Activate', value: 'activate' },
    { label: 'Inactivate', value: 'inactivate' },
    { label: 'Delete', value: 'delete' }
  ];

  pageSizeOptions: FilterOption[] = [
    { label: '25', value: '25' },
    { label: '50', value: '50' },
    { label: '100', value: '100' }
  ];

  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private offerSetListService: OfferSetListService,
    private offerRegistryService: OfferSetRegistryService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.accountId = Number(this.route.snapshot.paramMap.get('accountId'));
    this.initSearchDebounce();
    this.loadOfferSets();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initializes debounced search subscription
   */
  initSearchDebounce(): void {
    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe((term: string) => {
      this.searchTerm = term;
      this.pagination.page = 1;
      this.loadOfferSets();
    });
  }

  /**
   * Handles search input changes
   */
  onSearchChange(term: string): void {
    this.searchSubject.next(term);
  }

  /**
   * Selects a preset tab and loads data
   */
  selectPreset(tab: PresetTab): void {
    this.activePreset = tab.preset;
    this.selectedStatus = tab.status;
    this.selectedCondition = tab.condition;
    this.selectedVisibility = tab.visibility;
    this.pagination.page = 1;
    this.clearSelection();
    this.loadOfferSets();
  }

  /**
   * Handles filter dropdown changes
   */
  onFilterChange(): void {
    this.activePreset = '';
    this.pagination.page = 1;
    this.clearSelection();
    this.loadOfferSets();
  }

  /**
   * Handles pagination page change
   */
  onPageChange(event: any): void {
    this.pagination.page = (event.page + 1);
    this.pagination.pageSize = event.rows;
    this.clearSelection();
    this.loadOfferSets();
  }

  /**
   * Loads offer sets from the API
   */
  loadOfferSets(): void {
    this.isLoading = true;
    this.offerSetListService.getOfferSetList(
      this.accountId,
      this.activePreset,
      this.selectedStatus,
      this.selectedCondition,
      this.selectedVisibility,
      this.searchTerm,
      this.pagination.page,
      this.pagination.pageSize
    ).subscribe(
      (response) => {
        this.items = response.items;
        this.pagination = response.pagination;
        this.isLoading = false;
        this.offerRegistryService.callOfferRegistryService(this.accountId).subscribe(
          () => {
          },
          () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to sync Offer Sets.',
              life: 5000
            });
          }
        );
      },
      () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load Offer Sets.',
          life: 5000
        });
        this.isLoading = false;
      }
    );
  }

  /**
   * Toggles selection of all visible items
   */
  toggleSelectAll(): void {
    this.allSelected = !this.allSelected;
    if (this.allSelected) {
      this.selectedItems = [...this.items];
    } else {
      this.selectedItems = [];
    }
  }

  /**
   * Clears all selections
   */
  clearSelection(): void {
    this.selectedItems = [];
    this.allSelected = false;
  }

  /**
   * Checks if a specific item is selected
   */
  isItemSelected(item: OfferSetListItem): boolean {
    return this.selectedItems.some(selected => selected.id === item.id);
  }

  /**
   * Toggles selection of a single item
   */
  toggleItemSelection(item: OfferSetListItem): void {
    if (this.isItemSelected(item)) {
      this.selectedItems = this.selectedItems.filter(selected => selected.id !== item.id);
    } else {
      this.selectedItems = [...this.selectedItems, item];
    }
    this.allSelected = this.selectedItems.length === this.items.length;
  }

  /**
   * Toggles the active status of a single offer set
   */
  toggleStatus(item: OfferSetListItem): void {
    const newAction = item.data.active ? 'inactivate' : 'activate';
    const request: OfferSetBulkActionRequest = {
      accountId: this.accountId,
      action: newAction,
      offerSetIds: [item.id]
    };
    this.offerSetListService.executeBulkAction(request).subscribe(
      () => {
        this.loadOfferSets();
      },
      () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update Offer Set status.',
          life: 5000
        });
      }
    );
  }

  /**
   * Navigates to the edit page for an offer set
   */
  editOfferSet(item: OfferSetListItem): void {
    const adminDomain = environment.launchcontrolAdminDomain;
    const accountId = this.accountId;
    const offerSetId = item?.id;

    if (!adminDomain || !accountId || !offerSetId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Navigation Error',
        detail: 'Missing data required to open the editor.',
        life: 4000
      });

      return;
    }

    const url = `http://${adminDomain}/new-specials/${accountId}#/specials/edit/${offerSetId}`;

    window.location.href = url;
  }

  /**
   * Opens the disclosure dialog with the item's fullDisclosure content
   */
  openDisclosure(item: OfferSetListItem): void {
    const title = item?.data?.displayTitle || 'Disclosure';
    const content = item?.data?.fullDisclosure || '';

    this.disclosureTitle = title;
    this.disclosureHtml = this.sanitizer.bypassSecurityTrustHtml(content);
    this.disclosureDialogVisible = true;
  }

  /**
   * Closes the disclosure dialog
   */
  closeDisclosure(): void {
    this.disclosureDialogVisible = false;
    this.disclosureTitle = '';
    this.disclosureHtml = '';
  }

  /**
   * Opens delete confirmation for a single offer set
   */
  openDeleteConfirmation(item: OfferSetListItem): void {
    this.confirmationService.confirm({
      key: 'deleteOfferSet',
      defaultFocus: 'none',
      message: `Are you sure you want to delete "${item.data.displayTitle}"?`,
      header: 'Delete Offer Set',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
      },
      acceptButtonProps: {
        label: 'Delete',
        severity: 'danger'
      },
      accept: () => {
        this.deleteOfferSet(item);
      },
      reject: () => {
        this.confirmationService.close();
      }
    });
  }

  /**
   * Deletes a single offer set
   */
  deleteOfferSet(item: OfferSetListItem): void {
    const request: OfferSetBulkActionRequest = {
      accountId: this.accountId,
      action: 'delete',
      offerSetIds: [item.id]
    };
    this.offerSetListService.executeBulkAction(request).subscribe(
      () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Offer Set deleted.',
          life: 3000
        });
        this.loadOfferSets();
      },
      () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to delete Offer Set.',
          life: 5000
        });
      }
    );
  }

  /**
   * Applies the selected bulk action to selected items
   */
  applyBulkAction(): void {
    if (!this.selectedBulkAction || this.selectedItems.length === 0) {
      return;
    }

    if (this.selectedBulkAction === 'delete') {
      this.openBulkDeleteConfirmation();

      return;
    }

    this.executeBulkAction();
  }

  /**
   * Opens bulk delete confirmation dialog
   */
  openBulkDeleteConfirmation(): void {
    this.confirmationService.confirm({
      key: 'bulkDeleteOfferSets',
      defaultFocus: 'none',
      message: `Are you sure you want to delete ${this.selectedItems.length} Offer Set(s)?`,
      header: 'Delete Offer Sets',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
      },
      acceptButtonProps: {
        label: 'Delete',
        severity: 'danger'
      },
      accept: () => {
        this.executeBulkAction();
      },
      reject: () => {
        this.confirmationService.close();
      }
    });
  }

  /**
   * Executes the bulk action API call
   */
  executeBulkAction(): void {
    const selectedIds = this.selectedItems.map(item => item.id);
    const request: OfferSetBulkActionRequest = {
      accountId: this.accountId,
      action: this.selectedBulkAction,
      offerSetIds: selectedIds
    };
    this.offerSetListService.executeBulkAction(request).subscribe(
      (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `${response.successCount} Offer Set(s) updated successfully.`,
          life: 3000
        });
        if (response.failureCount > 0) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Warning',
            detail: `${response.failureCount} Offer Set(s) failed to update.`,
            life: 5000
          });
        }
        this.selectedBulkAction = '';
        this.clearSelection();
        this.loadOfferSets();
      },
      () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to execute bulk action.',
          life: 5000
        });
      }
    );
  }

  /**
   * Navigates to the inventory view
   */
  viewInventory(): void {
    window.location.href = `//${environment.launchcontrolAdminDomain}/mpop/${this.accountId}#/inventory`;
  }

  /**
   * Navigates to the build new offer set page
   */
  buildNewOfferSet(): void {
    window.location.href = `//${environment.launchcontrolAdminDomain}/mpop/${this.accountId}#/new-specials/create`;
  }
}
